import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import styles from "./Account.module.css"; // Import the CSS module
import Profile from "./Profile";
import Organisation from "./Organisation";
import Notification from "./Notification";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import TransferRight from "./TransferRight";
import DeleteAccount from "./DeleteAccount";
import img1 from "../../../shared/assets/icons/client-logo.webp";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import axios from "axios";
import GlobalSnackbar from "../../../shared/utils/GlobalSnackbar";

const AccountSettings = ({ onLogoImg }) => {
  const [activeTab, setActiveTab] = useState("Profile"); // Default tab
  const [profileData, setProfileData] = useState(null);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const serverURL = process.env.REACT_APP_URL;

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleProfileData = (data) => {
    setProfileData(data);
    if (onLogoImg) onLogoImg(data);
  };

  const [isUserRole, setIsUserRole] = useState(null);

  useEffect(() => {
    const userRole = localStorage.getItem("userRole");
    setIsUserRole(userRole);
  }, []);

  const renderContent = () => {
    switch (activeTab) {
      case "Profile":
        return <Profile onProfileData={handleProfileData} />;
      case "Organisation":
        return <Organisation />;
      // case "Notification":
      //   return <Notification />;
      case "Rights":
        return <TransferRight />;
      case "Delete":
        return (
          <DeleteAccount onTransferAccount={() => handleTabChange("Rights")} />
        );
      default:
        return <Profile onProfileData={handleProfileData} />;
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(false);
  };

  const handleInputChange = async (e) => {
    const { files } = e.target;
    if (files?.length > 0) {
      let file = files[0];
      if (file.size > 2 * 1024 * 1024) {
        setSnackbar({
          open: true,
          message: "Image must be less then 2 MB size",
          severity: "error",
        });
        return;
      }

      setIsImageLoading(true);
      const formDataNew = new FormData();
      formDataNew.append("files", file);
      try {
        const token = localStorage.getItem("token");
        const response = await axios.post(
          `${process.env.REACT_APP_URL}/upload`,
          formDataNew,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response.data.success) {
          // ✅ STEP 1: Extract uploaded image URL
          const imageUrl = response.data.data[0];

          if (imageUrl) {
            // ✅ STEP 2: Call PUT API to update the profile
            await axios.patch(
              `${serverURL}/organization/update-logo/${localStorage.getItem(
                "organisationId"
              )}`,
              {
                brandLogo: imageUrl,
              }
            );

            if (onLogoImg) onLogoImg(imageUrl);
            setIsImageLoading(false);

            // ✅ Optional: Update UI state or show notification
            setProfileData(imageUrl);
            setSnackbar({
              open: true,
              message: "Organization updated successfully.",
              severity: "success",
            });
          }
        }
      } catch (err) {
        setIsImageLoading(false);
        console.error("Error uploading file", err);
      }
    }
  };

  return (
    <Box className={styles.root}>
      <h1 className="mb-4">Account Setting</h1>
      {/* Header Section */}
      <Box className={styles.header}>
        <label>
          <div className=" w-20 h-20  mt-10"></div>
        </label>
        <div className="relative w-20 h-20 ml-3 mt-10 ">
          {isImageLoading ? (
            <div className="w-full h-full flex justify-center bg-white items-center rounded-full object-cover border-4 border-blue-500 border-solid">
              {/* <img
                src={"https://via.placeholder.com/80"} // fallback image
                alt="Profile"
                className="w-full h-full rounded-full object-cover border-4 border-blue-500 border-solid"
              /> */}
              <CircularProgress size={25} />
            </div>
          ) : profileData ? (
            <img
              src={profileData || "https://via.placeholder.com/80"} // fallback image
              alt="Profile"
              className="w-full h-full rounded-full object-cover border-4 border-blue-500 border-solid"
            />
          ) : (
            <CameraAltIcon className="text-gray-500 " fontSize="medium" />
          )}

          <label className="absolute bottom-0 right-0 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md cursor-pointer">
            <CameraAltIcon fontSize="small" className="text-gray-600" />
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleInputChange}
            />
          </label>
        </div>
      </Box>
      {/* <Box className={styles.header}>
        <div className="relative w-20 h-20 mx-auto mt-10">
          <img
            src={profileData || "https://via.placeholder.com/80"} // fallback image
            alt="Profile"
            className="w-full h-full rounded-full object-cover border-4 border-blue-500 border-solid"
          />

          <label className="absolute bottom-0 right-0 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md cursor-pointer">
            <CameraAltIcon fontSize="small" className="text-gray-600" />
            <input
              type="file"
              accept="image/*"
              className="absolute inset-0 opacity-0 cursor-pointer"
              onChange={handleInputChange}
            />
          </label>
        </div>
      </Box> */}

      {/* Scrollable Form Section */}
      <Box className={`shadow-lg ${styles.accountFormContainer}`}>
        <div className="flex justify-between items-center bg-[var(--secondary-color)]">
          <Stack className={styles.buttonContainer} direction="row" spacing={1}>
            <Chip
              label="Profile"
              style={{
                background:
                  activeTab === "Profile"
                    ? "var(--heading-color)"
                    : "var(--empty-color)",
                color:
                  activeTab === "Profile"
                    ? "var(--secondary-color)"
                    : "var(--heading-color)",
              }}
              onClick={() => setActiveTab("Profile")}
            />
            <Chip
              label="Organisation"
              style={{
                background:
                  activeTab === "Organisation"
                    ? "var(--heading-color)"
                    : "var(--empty-color)",
                color:
                  activeTab === "Organisation"
                    ? "var(--secondary-color)"
                    : "var(--heading-color)",
              }}
              onClick={() => setActiveTab("Organisation")}
            />
            {/* <Chip
              label="Notification"
              style={{
                background:
                  activeTab === "Notification"
                    ? "var(--heading-color)"
                    : "var(--empty-color)",
                color:
                  activeTab === "Notification"
                    ? "var(--secondary-color)"
                    : "var(--heading-color)",
              }}
              onClick={() => setActiveTab("Notification")}
            /> */}
            {/* <Chip
              label="Transfer Rights"
              style={{
                background:
                  activeTab === "Rights"
                    ? "var(--heading-color)"
                    : "var(--empty-color)",
                color:
                  activeTab === "Rights"
                    ? "var(--secondary-color)"
                    : "var(--heading-color)",
              }}
              onClick={() => setActiveTab("Rights")}
            /> */}
            {/* <Chip
              label="Delete Account"
              style={{
                background:
                  activeTab === "Delete"
                    ? "var(--heading-color)"
                    : "var(--empty-color)",
                color:
                  activeTab === "Delete"
                    ? "var(--secondary-color)"
                    : "var(--heading-color)",
              }}
              onClick={() => setActiveTab("Delete")}
            /> */}
          </Stack>
          <Stack className={styles.buttonContainer} direction="row" spacing={1}>
            {/* <Chip
              label="Super Admin"
              style={{
                background: "var(--primary-color)",
                color: "var(--secondary-color)",
              }}
            /> */}
            {isUserRole === "1" && (
              // <Chip
              //   label="Super Admin"
              //   style={{
              //     color: "var(--secondary-color)",
              //     background: "var(--primary-color)",
              //   }}
              // />

              <Chip
                label="Super Admin"
                style={{
                  color: "var(--secondary-color)",
                  background: "var(--primary-color)",
                  marginRight: "1rem",
                }}
              />
            )}
            {isUserRole === "2" && (
              <Chip
                label="Admin"
                style={{
                  color: "var(--secondary-color)",
                  background: "var(--primary-color)",
                  marginRight: "1rem",
                }}
              />
            )}
            {isUserRole === "3" && (
              <Chip
                label="Member"
                style={{
                  color: "var(--secondary-color)",
                  background: "var(--primary-color)",
                  marginRight: "1rem",
                }}
              />
            )}
          </Stack>
        </div>
        <hr className="mx-2"></hr>
        {/* Render the active component based on selected tab */}
        <div className="bg-[var(--secondary-color)]">{renderContent()}</div>
      </Box>
      <GlobalSnackbar snackbar={snackbar} handleClose={handleSnackbarClose} />
    </Box>
  );
};

export default AccountSettings;
